import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useReducedMotion, useSpring, useTransform } from 'framer-motion';
import { useAppearance } from './AppearanceProvider';
import './flow-motion.css';

export function useStillMotion() {
  const reduced = useReducedMotion(), { preferences } = useAppearance();
  return !!(reduced || preferences.quiet);
}

// Only the light moves. Text, charts, menus and their hit areas stay in place.
export function FlowSurface({ as: Surface = 'section', children, className = '', ambient = false, ...props }) {
  const root = useRef(null), still = useStillMotion(), [visible, setVisible] = useState(true);
  const x = useMotionValue(100), y = useMotionValue(70), active = useMotionValue(0);
  const lightX = useSpring(x, { stiffness: 160, damping: 30 });
  const lightY = useSpring(y, { stiffness: 160, damping: 30 });
  const opacity = useSpring(active, { stiffness: 150, damping: 28 });
  useEffect(() => {
    if (!root.current || typeof IntersectionObserver !== 'function') return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting));
    observer.observe(root.current); return () => observer.disconnect();
  }, []);
  useEffect(() => { if (still || !visible) active.set(0); }, [still, visible, active]);
  return <Surface {...props} ref={root} className={`tl-flow-surface ${className}`} data-flow-active={!still && visible} onPointerMove={event => {
    if (event.pointerType !== 'mouse' || event.buttons || still || !visible || document.hidden) return;
    const bounds = root.current.getBoundingClientRect();
    x.set(event.clientX - bounds.left); y.set(event.clientY - bounds.top); active.set(1);
  }} onPointerLeave={() => active.set(0)} onPointerDown={() => active.set(0)}>
    {children}
    <span className="tl-flow-light" aria-hidden="true">
      {ambient && <span className="tl-flow-ambient"><i/><i/></span>}
      <motion.span className="tl-flow-cursor" style={{ x: lightX, y: lightY, opacity: still ? 0 : opacity }}/>
      <span className="tl-flow-rim"/>
    </span>
  </Surface>;
}

export function FlowTabs({ label, value, options, onChange, className = '', style }) {
  const id = useId(), still = useStillMotion();
  return <div className={`tl-flow-tabs ${className}`} style={style} role="group" aria-label={label}>
    {options.map(option => <button key={String(option.value)} type="button" aria-label={option.ariaLabel} aria-pressed={value === option.value} disabled={option.disabled} className={value === option.value ? 'is-active' : ''} onClick={() => onChange(option.value)}>
      {value === option.value && <motion.span aria-hidden="true" className="tl-flow-selection" layoutId={`flow-tabs-${id}`} transition={still ? {duration: 0} : {type: 'spring', stiffness: 460, damping: 38}}/>}
      <span className="tl-flow-option-label">{option.label}</span>
    </button>)}
  </div>;
}

// Refresh content in place. A drag already owns its transition and must not
// receive a second animation; focus and calendar selection stay untouched.
export function useFlowRefresh(ref, identity) {
  const previous = useRef(identity), still = useStillMotion();
  useLayoutEffect(() => {
    const before = previous.current; previous.current = identity;
    const node = ref.current;
    if (Object.is(before, identity) || still || !node?.animate || node.closest('[data-swipe-motion]')) return;
    const direction = typeof identity === 'number' && identity < before ? -1 : 1;
    const animation = node.animate([{opacity:.3,transform:`translateX(${direction * 12}px)`},{opacity:1,transform:'translateX(0)'}], {duration:260,easing:'cubic-bezier(.16,1,.3,1)'});
    return () => animation.cancel();
  }, [ref, identity, still]);
}

export function dockInfluence(distance) {
  if (!Number.isFinite(distance)) return 0;
  const proximity = Math.max(0, 1 - Math.abs(distance) / 145);
  return proximity * proximity;
}

export function FlowDockContent({ cursor, still, children }) {
  const node = useRef(null);
  const proximity = useTransform(cursor, position => {
    if (still || position < 0 || !node.current) return 0;
    const bounds = node.current.parentElement.getBoundingClientRect();
    return dockInfluence(position - (bounds.left + bounds.width / 2));
  });
  const lift = useSpring(useTransform(proximity, value => -7 * value), {stiffness: 390, damping: 29});
  const scale = useSpring(useTransform(proximity, value => 1 + .12 * value), {stiffness: 390, damping: 29});
  return <motion.span ref={node} className="tl-orbit-dock-content tl-flow-dock-content" style={{y: still ? 0 : lift, scale: still ? 1 : scale}}>{children}</motion.span>;
}
